import { tr, trf } from './i18n';
import { jourIso, rapprocheSorties } from './rapprochement-sorties';

/**
 * Les relevés de banque gardés, tels que le classeur les montre et que la
 * comptable les reçoit.
 */

const MOIS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

/** « 2026-10-02 » → « Octobre 2026 ». */
const moisDe = (jour) => tr(MOIS[+jour.slice(5, 7) - 1] || '') + ' ' + jour.slice(0, 4);

/**
 * Le titre d'un relevé : son mois (« Octobre 2026 »), ou ses mois s'il en
 * couvre plusieurs, puis sa banque quand le fichier la nomme.
 *
 * @param {object} r relevé gardé ({debut, fin, banque, nomFichier})
 * @returns {string} le titre
 */
export function titreDuReleve(r) {
  const debut = jourIso(r?.debut || r?.fin);
  const fin = jourIso(r?.fin || r?.debut);
  let periode;
  if (!debut) periode = r?.nomFichier || '';
  else if (debut.slice(0, 7) === fin.slice(0, 7)) periode = moisDe(debut);
  else if (debut.slice(0, 4) === fin.slice(0, 4))
    periode = tr(MOIS[+debut.slice(5, 7) - 1]) + ' – ' + moisDe(fin);
  else periode = moisDe(debut) + ' – ' + moisDe(fin);
  return r?.banque ? periode + ' · ' + r.banque : periode;
}

/**
 * L'année d'un relevé : celle de son dernier mouvement, à défaut celle de
 * son import.
 *
 * @param {object} r relevé gardé
 * @returns {number} l'année
 */
export function anneeDuReleve(r) {
  const jour = jourIso(r?.fin || r?.debut) || String(r?.transmisLe || '').slice(0, 10);
  return +jour.slice(0, 4) || 0;
}

/** Ce à quoi une ligne a été reliée, sans la coche de l'écran. */
const sansCoche = (s) => String(s || '').replace(/^\s*✓\s*/, '');

/**
 * Les lignes d'un relevé pour le résumé envoyé à la comptable : chaque
 * mouvement de sa période, avec ce à quoi il a été relié — facture payée,
 * dépense, facture de fournisseur, comptable, facture d'un autre logiciel,
 * sortie privée. Les libellés sont ceux que l'onglet Banque affiche déjà.
 *
 * @param {object} r relevé gardé ({debut, fin})
 * @param {object[]} mouvements relevé gardé dans la société (soc.releveBancaire)
 * @param {object} [ctx] ce qui sert à relier
 * @param {object[]} [ctx.factures] factures clients
 * @param {object} [ctx.comptable] fiche du comptable
 * @param {object[]} [ctx.chantiers] chantiers
 * @param {object[]} [ctx.frais] dépenses
 * @param {object[]} [ctx.achats] factures de fournisseurs jointes aux offres et factures
 * @returns {{date: string, libelle: string, montant: number, sens: string, reliee: string}[]}
 *   les lignes, vides si le relevé n'a pas de période
 */
export function lignesPourLaComptable(r, mouvements, ctx = {}) {
  const debut = jourIso(r?.debut);
  const fin = jourIso(r?.fin);
  if (!debut || !fin) return [];
  const tx = (Array.isArray(mouvements) ? mouvements : []).filter((t) => {
    const j = jourIso(t?.date);
    return j && j >= debut && j <= fin;
  });
  const { factures, comptable, chantiers, frais, achats } = ctx;
  const liens = rapprocheSorties(tx, { comptable, chantiers, frais, achats });
  return tx.map((t, i) => {
    const lien = liens[i];
    let reliee = '';
    if (t.sens === 'CRDT') {
      const f =
        t.factureId != null
          ? (factures || []).find((x) => String(x.id) === String(t.factureId))
          : null;
      if (f) reliee = trf('Facture n° {0}', f.num || f.id);
      else if (t.traite === 'ailleurs') reliee = tr('✓ Facture d’un autre logiciel');
    } else if (t.traite === 'prive') {
      reliee = tr('✓ Déjà comptée ou privée');
    } else if (lien?.incertain) {
      reliee = trf('✓ Déjà comptée : {0} (même montant)', lien.libelle);
    } else if (lien?.genre === 'comptable') {
      reliee = tr('Comptable / fiduciaire');
    } else if (lien?.genre === 'fournisseur') {
      reliee = trf(
        '✓ Facture fournisseur {0} · {1} — déjà comptée',
        lien.fournisseur,
        lien.chantier,
      );
    } else if (lien?.genre === 'soustraitant') {
      reliee = trf('✓ Sous-traitant {0} · {1} — déjà compté', lien.fournisseur, lien.chantier);
    } else if (lien?.genre === 'piece') {
      reliee = trf(
        '✓ Facture {0} jointe à une offre ou une facture — déjà comptée',
        lien.fournisseur,
      );
    } else if (lien?.genre === 'depense') {
      reliee = trf('✓ Déjà en dépense : {0}', lien.libelle);
    }
    return {
      date: jourIso(t.date),
      libelle: String(t.label || '').slice(0, 300),
      montant: Math.abs(+t.montant || 0),
      sens: t.sens === 'DBIT' ? 'DBIT' : 'CRDT',
      reliee: sansCoche(reliee).slice(0, 200),
    };
  });
}
